import api from '../../../services/api';
import { checkToken } from '@/utils/lambdaUtils';
import logger from '@/utils/logger';

const baseUrl = `${import.meta.env.VITE_APP_API_BASE_URL}/cex/`;

//#region Cex Report
/**
 * Fetches the CEX report for the given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch.
 * @returns {Promise<Object>} The data of the fetched report.
 * @throws {Error} If the reportId is not provided or if there is an error fetching the report.
 */
export const getCexReport = async (reportId) => {
  if (!reportId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.get(`${baseUrl}annualreport?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching CEX report:', error);
    throw error;
  }
};

/**
 * Creates an CEX report for the given organization, period, contact, and role.
 *
 * @param {string} organizationId - The ID of the organization.
 * @param {string} periodId - The ID of the period.
 * @param {string} contactId - The ID of the contact.
 * @param {string} roleId - The ID of the role.
 * @returns {Promise<Object>} The response data from the API.
 * @throws {Error} If any of the required parameters are missing.
 */
export const createCexReport = async (organizationId, periodId, contactId, roleId) => {
  if (!organizationId || !periodId || !contactId || !roleId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}createannualreport`, { organizationId, periodId, contactId, roleId });
    return response.data;
  } catch (error) {
    logger.error('Error creating annual report', error);
  }
};

/**
 * Updates the status of a report.
 *
 * @async
 * @function
 * @param {number|string} reportId - The unique identifier of the report to update.
 * @param {number|string} statusId - The new status ID to set for the report.
 * @param {boolean} isCertified - Indicates whether the report is certified.
 * @returns {Promise<Object>} The response data from the API after updating the report status.
 * @throws Will throw an error if the API request fails.
 */
export const updateReportStatus = async(reportId, statusId, isCertified) => {
  try {
    await checkToken();
    const data = {
      reportId,
      reportStatusId: statusId,
      isCertified,
    };
    const response = await api.post(`${baseUrl}updatereportstatus`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating report status', error);
    throw error;
  }
};

/**
 * Updates the reporting months for a specific CFO in a report.
 *
 * @async
 * @function updateCfoReportingMonths
 * @param {string|number} reportId - The unique identifier of the report.
 * @param {string} cfoName - The name of the CFO whose reporting months are being updated.
 * @param {Array<string|number>} reportingMonths - An array of reporting months to be set for the CFO.
 * @returns {Promise<any>} The response data from the API after updating the reporting months.
 * @throws Will throw an error if the API call fails.
 */
export const updateCfoReportingMonths = async(reportId, cfoName, reportingMonths) => {
  try {
    await checkToken();
    const data = {
      reportId,
      cfoName,
      reportingMonths,
    };
    const response = await api.post(`${baseUrl}updatecforeportingmonths`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating CFO reporting months', error);
    throw error;
  }
};

//#endregion Cex Report

//#region Contacts

/**
 * Fetches the contacts for a given CEX report.
 *
 * @param {string} reportId - The ID of the report to fetch contacts for.
 * @returns {Promise<Object>} A promise that resolves to the report contacts data.
 * @throws {Error} If the reportId is not provided or if there is an error fetching the data.
 */
export const getReportContacts = async (reportId) => {
  if (!reportId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.get(`${baseUrl}annualreportcontacts?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching CEX report contacts:', error);
    throw error;
  }
};

/**
 * Fetches the available report contacts for a given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch contacts for.
 * @returns {Promise<Array>} A promise that resolves to an array of available report contacts.
 * @throws {Error} If there is an error fetching the report contacts or if the response data is invalid.
 */
export const getAvailableReportContacts = async (reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}availablereportcontacts?id=${encodeURIComponent(reportId)}`);
    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      throw new Error('Invalid response data');
    }
  } catch (error) {
    logger.error('Error getting available report contacts', error);
    throw error;
  }
};

/**
 * Adds a contact to a CEX report.
 *
 * @param {string} reportId - The ID of the report to which the contact will be added.
 * @param {Object} contact - The contact information to be added to the report.
 * @throws {Error} Throws an error if the required parameters are missing.
 * @throws {Error} Throws an error if there is an issue adding the contact.
 * @returns {Promise<Object>} The response data from the API.
 */
export const addReportContact = async (reportId, contactId, roleId) => {
  if (!reportId || !contactId || !roleId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}addreportcontact?id=${encodeURIComponent(reportId)}`, { reportId, contactId, roleId });
    return response.data;
  } catch (error) {
    logger.error('Error adding CEX report contact:', error);
    throw error;
  }
};

/**
 * Adds contacts to a report.
 *
 * @param {string} reportId - The ID of the report to which contacts will be added.
 * @param {Array<{contactId: string, roleId: string}>} contacts - An array of contact objects, each containing a contactId and roleId.
 * @returns {Promise<Object>} The response data from the API.
 * @throws {Error} If required parameters are missing or if there is an error adding report contacts.
 */
export const addReportContacts = async (reportId, contacts) => {
  if (!reportId || !contacts) {
    throw new Error('Missing required parameters');
  }

  try {
    const sanitizedContacts = contacts.map(({ contactId, roleId }) => ({ contactId, roleId }));
    await checkToken();
    const response = await api.post(`${baseUrl}addreportcontacts?id=${encodeURIComponent(reportId)}`, sanitizedContacts);
    return response.data;
  } catch (error) {
    logger.error('Error adding report contacts', error);
    throw error;
  }
};

/**
 * Updates the contact information for the CEX report.
 *
 * @param {string} reportId - The ID of the report to update.
 * @param {Object} updateContact - The contact information to update.
 * @throws {Error} If required parameters are missing.
 * @throws {Error} If there is an error updating the report contact.
 * @returns {Promise<Object>} The updated report contact data.
 */
export const updateReportContact = async (reportId, updateContact) => {
  if (!reportId || !updateContact) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatereportcontact?id=${encodeURIComponent(reportId)}`, updateContact);
    return response.data;
  } catch (error) {
    logger.error('Error updating report contact', error);
    throw error;
  }
};


/**
 * Removes a contact from the CEX report.
 *
 * @param {string} reportId - The ID of the report.
 * @param {string} contactId - The ID of the contact to be removed.
 * @throws {Error} If required parameters are missing.
 * @throws {Error} If there is an error removing the report contact.
 * @returns {Promise<Object>} The response data from the API.
 */
export const removeReportContact = async (reportId, contactId) => {
  if (!reportId || !contactId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}removereportcontact?id=${encodeURIComponent(reportId)}&contactId=${encodeURIComponent(contactId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error removing report contact:', error);
    throw error;
  }
};

//#endregion Contacts

//#region Facility Affiliates

/**
 * Fetches facility affiliates for a given report ID.
 *
 * @param {string} reportId - The ID of the report to fetch facility affiliates for.
 * @returns {Promise<Object>} A promise that resolves to the data of the facility affiliates.
 * @throws {Error} Throws an error if the reportId is missing or if there is an issue with the API request.
 */
export const getFacilityAffiliates = async (reportId) => {
  if (!reportId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.get(`${baseUrl}facilityaffiliates?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching CEX facility affiliates:', error);
    throw error;
  }
};

/**
 * Inserts an affiliate organization for a given report.
 *
 * @async
 * @function insertAffiliateOrganization
 * @param {string|number} reportId - The ID of the report to associate with the affiliate organization.
 * @param {string} affiliateName - The name of the affiliate organization to insert.
 * @param {string|number} affiliationTypeId - The type ID of the affiliation.
 * @returns {Promise<Object>} The response data from the API after inserting the affiliate organization.
 * @throws {Error} Throws an error if required parameters are missing or if the API call fails.
 */
export const insertAffiliateOrganization = async (reportId, affiliateName, affiliationTypeId) => {
  if (!reportId || !affiliateName || !affiliationTypeId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}insertaffiliateorganization`, { reportId, affiliateName, affiliationTypeId });
    return response.data;
  } catch (error) {
    logger.error('Error inserting affiliated organization:', error);
    throw error;
  }
};

/**
 * Updates the affiliation of a facility.
 *
 * @async
 * @param {Object} facilityAffiliate - The facility affiliation data to update.
 * @throws {Error} Throws an error if required parameters are missing or if the API call fails.
 * @returns {Promise<Object>} The response data from the API after updating the facility affiliation.
 */
export const updateFacilityAffiliation = async (facilityAffiliate) => {
  if (!facilityAffiliate) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatefacilityaffiliation`, facilityAffiliate);
    return response.data;
  } catch (error) {
    logger.error('Error updating facility affiliation:', error);
    throw error;
  }
};

/**
 * Fetches the list of available affiliates from the API.
 *
 * @async
 * @function
 * @returns {Promise<Object>} A promise that resolves to the data containing available affiliates.
 * @throws Will throw an error if the API request fails or the token check fails.
 */
export const getAvailableAffiliates = async () => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}availableaffiliates`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching available affiliates:', error);
    throw error;
  }
};

/**
 * Adds or updates a facility affiliate to a report.
 *
 * @param {string} reportId - The ID of the report.
 * @param {Object} facilityAffiliate - The facility affiliate data to be added.
 * @throws {Error} Throws an error if required parameters are missing.
 * @returns {Promise<Object>} The response data from the API.
 */
export const addUpdateFacilityAffiliate = async (facilityAffiliate) => {
  if (!facilityAffiliate) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}addupdatefacilityaffiliate`, facilityAffiliate);
    return response.data;
  } catch (error) {
    logger.error('Error adding CEX facility affiliate:', error);
    throw error;
  }
};

/**
 * Removes an affiliate from a facility report.
 *
 * @async
 * @param {string} reportId - The unique identifier of the report.
 * @param {string} affiliateId - The unique identifier of the affiliate to remove.
 * @returns {Promise<any>} The response data from the API.
 * @throws {Error} If required parameters are missing or if the API call fails.
 */
export const removeFacilityAffiliate = async (reportId, affiliateId) => {
  if (!reportId || !affiliateId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}removefacilityaffiliate?id=${encodeURIComponent(reportId)}&affiliateId=${encodeURIComponent(affiliateId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error removing CEX facility affiliate:', error);
    throw error;
  }
};

//#endregion Facility Affiliates